import clsx from "clsx";

// Interfaces
import { IActionsProps, IExtendedRenderOptionProps, IOption, IOptionsProps, IRenderOptionProps, ISmartBasicsProps } from "../interfaces";

// MUI
import { Checkbox, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";

// Components
import CustomRenderOption from "./CustomRenderOption";
import OptionActionControl from "../OptionActionControl";

// Utils
const getDisabled = (renderProps?: IOptionsProps | null, disabled = false, allDisabled = false) =>
	Boolean(renderProps?.["aria-disabled" as keyof object]) || disabled || allDisabled;
const getSelected = (renderProps?: IOptionsProps | null, selected = false, checked = false) =>
	Boolean(renderProps?.["aria-selected" as keyof object]) || selected || checked;

const getItemSelected = (selected = false, multiple = false, disabled = false) => {
	const isSelected = selected && !disabled;
	const isMultipleSelected = isSelected && multiple;
	const isSimpleSelected = isSelected && !multiple;
	return {
		isMultipleSelected,
		isSimpleSelected,
		isItemDisabled: disabled,
		isItemSelected: isSelected,
	};
};

const getActionControl = (
	actionsProps: IActionsProps | null = null,
	option: IOption | null = null,
	args: ISmartBasicsProps | null = null
) => {
	const { selected = false, disabled = false } = args || {};
	return actionsProps ? (
		<OptionActionControl
			{...actionsProps}
			isControlledAction={Boolean(actionsProps)}
			option={option}
			selected={selected}
			disabled={disabled}
		/>
	) : undefined;
};

function RenderCustomOrDefaultOption(props: Readonly<IExtendedRenderOptionProps>) {
	const {
		title = "",
		renderOptionCustomProps = undefined,
		args = undefined,
		option = undefined,
		disabled = false,
		selected = false,
		multipleSelected = false,
	} = props;
	const { customComponent = undefined } = renderOptionCustomProps || {};

	if (customComponent) {
		return (
			<CustomRenderOption
				title={title}
				disabled={disabled}
				selected={selected}
				multipleSelected={multipleSelected}
				renderOptionCustomProps={renderOptionCustomProps}
				args={args}
				option={option}
			/>
		);
	}

	return (
		<Stack width={1} direction={"row"}>
			<Typography
				noWrap
				className={clsx({
					"!font-sans !text-[14.5px]": true,
					"!text-default": !selected || multipleSelected,
					"!text-white": selected && !multipleSelected,
					"!text-disabled": disabled,
				})}
			>
				{title}
			</Typography>
		</Stack>
	);
}

export default function RenderOption(props: Readonly<IRenderOptionProps>) {
	const {
		renderProps = undefined,
		renderOptionCustomProps = undefined,
		actionsProps = undefined,
		args = undefined,
		option = undefined,
	} = props;
	const { className, ...optionProps } = renderProps || {};
	const { selectBy = "_id", showBy = "label", selected = false, multiple = false, disabled: allDisabled = false } = args || {};

	const {
		[selectBy as keyof object]: identifier,
		[showBy as keyof object]: title,
		checked = false,
		disabled = false,
		hidden = false,
		onClickOption = () => {},
	} = option || {};

	const isDisabled = getDisabled(renderProps, disabled, allDisabled);
	const isSelected = getSelected(renderProps, selected, checked);

	const labelId = `checkbox-list-label-${identifier}-${title}`;
	const { isItemDisabled, isItemSelected, isMultipleSelected, isSimpleSelected } = getItemSelected(isSelected, multiple, isDisabled);

	const secondaryAction = getActionControl(actionsProps, option, { selected: isSelected, disabled: isItemDisabled });

	const handleOptionClick = () => {
		onClickOption?.(option ?? null);
	};

	return (
		<ListItem
			{...optionProps}
			className={clsx({
				"!p-0 !opacity-100": true,
				"!bg-transparent": isItemDisabled,
				"!hidden": hidden,
				[className as string]: Boolean(className),
			})}
			secondaryAction={secondaryAction}
			disablePadding
		>
			<ListItemButton
				className={clsx({
					"!pl-[10px] !opacity-100": true,
					"!bg-strong-gray": isSimpleSelected,
					"!bg-table-hover": isMultipleSelected,
				})}
				dense
				disableRipple
				disableTouchRipple
				disabled={isItemDisabled}
				selected={isItemSelected}
				onClick={handleOptionClick}
			>
				{multiple && (
					<ListItemIcon sx={{ minWidth: "15px" }}>
						<Checkbox
							edge="start"
							size="small"
							className={clsx({
								"!text-primary": isItemSelected,
								"[&_svg]:!text-disabled": isItemDisabled,
							})}
							checked={isSelected}
							disabled={isItemDisabled}
							tabIndex={-1}
							disableRipple
							inputProps={{ "aria-labelledby": labelId }}
							sx={{ padding: 0, px: "10px" }}
						/>
					</ListItemIcon>
				)}
				<ListItemText id={labelId} disableTypography sx={{ margin: 0 }}>
					<RenderCustomOrDefaultOption
						title={title}
						disabled={isItemDisabled}
						selected={isItemSelected}
						multipleSelected={isMultipleSelected}
						args={args}
						option={option}
						renderOptionCustomProps={renderOptionCustomProps}
					/>
				</ListItemText>
			</ListItemButton>
		</ListItem>
	);
}
